import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { landingPagesData } from './landingPagesData'
import { getLanguageBasedOnDomain } from '@utils/getDomainExtension'
import { hreflangMappper } from '@constants/hreflangMapper.consts'
import { MapLocation } from '@customTypes/mapLocation.interface'

type LandingPageTranslation = {
    name: string
    title: string
    description: string
    // meta tags can be in a different language than the page
    metaTitle: string
    metaDescription: string
}

type LandingPage = (typeof landingPagesData)[number] & {
    translations: LandingPageTranslation
}

export const useLandingPageMeta = (): {
    landingPage: LandingPage | null
    metaTags: {
        metaTitle: string
        metaDescription: string
    } | null
} => {
    const { t, i18n } = useTranslation()
    const location = useLocation()
    const pathname = location.pathname.toLowerCase()

    const landingPage = useMemo(() => {
        const city =
            landingPagesData.find(
                (c) => pathname.includes(c.routeEN.toLowerCase()) || pathname.includes(c.routeDE.toLowerCase())
            ) || null

        if (!city) return null

        const translations = t(`landingPages.${city.key}`, { returnObjects: true }) as LandingPageTranslation

        return {
            ...city,
            translations
        }
    }, [pathname, t])

    const metaTags = useMemo(() => {
        if (!landingPage) return null

        const seoLang = i18n.getFixedT(getLanguageBasedOnDomain())
        const translations = seoLang(`landingPages.${landingPage.key}`, {
            returnObjects: true
        }) as LandingPageTranslation

        return {
            metaTitle: translations.metaTitle,
            metaDescription: translations.metaDescription
        }
    }, [landingPage, i18n])

    return { landingPage, metaTags }
}

export const useHreflangs = (landingPage: LandingPage | null) =>
    useMemo(() => {
        if (!landingPage) return []
        return Object.entries(hreflangMappper).map(([key, { hreflang, domain }]) => ({
            hreflang,
            href: `${domain}${['de', 'ch', 'at'].includes(key) ? landingPage.routeDE : landingPage.routeEN}`
        }))
    }, [landingPage])

export const useSearchedLocation = (landingPage: LandingPage | null): MapLocation =>
    useMemo(
        () => ({
            name: landingPage?.key || '',
            lat: landingPage?.lat || 0,
            lng: landingPage?.lng || 0,
            lotId: ''
        }),
        [landingPage]
    )
